import type { EnumUnion } from '@dresscode-frontend/utility-types';
import { match } from 'ts-pattern';
import type { IconName } from '@/components/Icon/iconMap';
import type { ApiDeepRequiredResponseBody } from '@/lib/api/api-type-helper';
export const DeviceType = {
  COMPUTER: 'COMPUTER',
  MOBILE: 'MOBILE',
  MONITOR: 'MONITOR',
  SERVER: 'SERVER',
  OTHER: 'OTHER'
} as const;
export type DeviceType = EnumUnion<typeof DeviceType>;
export const DeviceComputerType = {
  LAPTOP: 'LAPTOP',
  WORKSTATION: 'WORKSTATION',
  MOBILE: 'MOBILE',
  TABLET: 'TABLET'
} as const;
export type DeviceComputerType = EnumUnion<typeof DeviceComputerType>;
export const DeviceGenre = {
  LAPTOP: 'LAPTOP',
  WORKSTATION: 'WORKSTATION',
  MONITOR: 'MONITOR',
  MOBILE: 'MOBILE',
  TABLET: 'TABLET',
  SERVER: 'SERVER',
  OTHER: 'OTHER'
} as const;
export type DeviceGenre = (typeof DeviceGenre)[keyof typeof DeviceGenre];
export const OSPlatformType = {
  WINDOWS: 'WINDOWS',
  MACOS: 'MACOS',
  LINUX: 'LINUX',
  IOS: 'IOS',
  ANDROID: 'ANDROID',
  TVOS: 'TVOS',
  WATCHOS: 'WATCHOS',
  VISIONOS: 'VISIONOS',
  OTHER: 'OTHER'
} as const satisfies Record<string, ApiDeepRequiredResponseBody<'/v2/devices/{id}', 'get'>['computer']['osPlatform']>;
export type OSPlatformType = EnumUnion<typeof OSPlatformType>;
export const PortType = {
  USB_TYPE_A: 'USB_TYPE_A',
  USB_TYPE_B: 'USB_TYPE_B',
  USB_TYPE_C: 'USB_TYPE_C',
  THUNDERBOLT: 'THUNDERBOLT',
  MINI_USB: 'MINI_USB',
  MICRO_USB: 'MICRO_USB',
  USB_MICRO_B: 'USB_MICRO_B',
  HDMI: 'HDMI',
  DISPLAYPORT: 'DISPLAYPORT',
  MINI_DISPLAYPORT: 'MINI_DISPLAYPORT',
  VGA: 'VGA',
  DVI: 'DVI',
  AUDIO: 'AUDIO',
  LAN: 'LAN'
} as const satisfies Record<string, ApiDeepRequiredResponseBody<'/v2/devices/{id}', 'get'>['ports'][number]['type']>;
export type PortType = EnumUnion<typeof PortType>;
export const PortTypeLabel = {
  USB_TYPE_A: 'USB Type A',
  USB_TYPE_B: 'USB Type B',
  USB_TYPE_C: 'USB Type C',
  THUNDERBOLT: 'Thunderbolt',
  MINI_USB: 'Mini USB',
  MICRO_USB: 'Micro USB',
  USB_MICRO_B: 'USB Micro B',
  HDMI: 'HDMI',
  DISPLAYPORT: 'DisplayPort',
  MINI_DISPLAYPORT: 'Mini DisplayPort',
  VGA: 'VGA',
  DVI: 'DVI',
  AUDIO: 'Audio',
  LAN: 'LAN'
} as const satisfies Record<PortType, string>;
export const NetworkInterfaceType = {
  UNKNOWN: 'UNKNOWN',
  ETHERNET: 'ETHERNET',
  IEEE_802_11: 'IEEE_802_11',
  IEEE_802_15_1: 'IEEE_802_15_1'
} as const satisfies Record<string, ApiDeepRequiredResponseBody<'/v2/devices/{id}', 'get'>['networkInterfaces'][number]['type']>;
export type NetworkInterfaceType = EnumUnion<typeof NetworkInterfaceType>;
export const StorageType = {
  SSD: 'SSD',
  HDD: 'HDD',
  UNKNOWN: 'UNKNOWN'
} as const satisfies Record<string, ApiDeepRequiredResponseBody<'/v2/devices/{id}', 'get'>['storages'][number]['type']>;
export type StorageType = EnumUnion<typeof StorageType>;
export const ProcurementType = {
  PURCHASE: 'PURCHASE',
  LEASE: 'LEASE',
  RENTAL: 'RENTAL',
  BYOD: 'BYOD',
  OTHER: 'OTHER'
} as const;
export type ProcurementType = EnumUnion<typeof ProcurementType>;
export const DeviceStatus = {
  IN_DISTRIBUTION: 'IN_DISTRIBUTION',
  IN_STOCK: 'IN_STOCK',
  IN_USED: 'IN_USED',
  IN_PLACED: 'IN_PLACED',
  FAULTY: 'IN_TROUBLED',
  REFURBISHING: 'REFURBISHING',
  UNDER_REPAIR: 'UNDER_REPAIR',
  DECOMMISSIONED: 'DECOMMISSIONED',
  DELETED: 'DELETED'
} as const;
export type DeviceStatus = EnumUnion<typeof DeviceStatus>;
const DEVICE_STATUS_I18N_KEY_INTERNAL = {
  IN_DISTRIBUTION: 'inDistribution',
  IN_STOCK: 'inStock',
  IN_USED: 'inUsed',
  IN_PLACED: 'inPlaced',
  IN_TROUBLED: 'faulty',
  REFURBISHING: 'refurbishing',
  UNDER_REPAIR: 'underRepair',
  DECOMMISSIONED: 'decommissioned',
  DELETED: 'deleted'
} as const satisfies Record<DeviceStatus, string>;
export type DeviceStatusI18nKey = (typeof DEVICE_STATUS_I18N_KEY_INTERNAL)[keyof typeof DEVICE_STATUS_I18N_KEY_INTERNAL];
export const DEVICE_STATUS_I18N_KEY: Record<string, DeviceStatusI18nKey> = DEVICE_STATUS_I18N_KEY_INTERNAL;
export const DeviceWarehousingPlaceStorageType = {
  IN_STOCK: 'IN_STOCK',
  REFURBISHING: 'REFURBISHING',
  TEMPORARILY_STORED: 'TEMPORARILY_STORED',
  WAITING_FOR_DISPOSAL: 'WAITING_FOR_DISPOSAL'
} as const;
export type DeviceWarehousingPlaceStorageType = EnumUnion<typeof DeviceWarehousingPlaceStorageType>;

//########################################################
// Device Activity Type
// （こういう系、featuresに移すのとどちらがいいのか悩み中）
//########################################################
export const DeviceActivityType = {
  ORDERED: 'ORDERED',
  PROCURED: 'PROCURED',
  DECOMMISSIONED: 'DECOMMISSIONED',
  TROUBLED: 'TROUBLED',
  RECOVERED: 'RECOVERED',
  DELETED: 'DELETED'
} as const;
export type DeviceActivityType = EnumUnion<typeof DeviceActivityType>;
export const getDeviceActivityIcon = (type: DeviceActivityType): IconName => match(type).with('ORDERED', () => 'PackagePlus' as const).with('PROCURED', () => 'PackagePlus' as const).with('DECOMMISSIONED', () => 'Trash2Icon' as const).with('TROUBLED', () => 'HeartMinusIcon' as const).with('RECOVERED', () => 'HeartPulseIcon' as const).with('DELETED', () => 'Trash2Icon' as const).exhaustive();

//########################################################
// Network And SIM Type
//########################################################
export type NetworkAndSimType = {
  mobileCarrier: string | null;
  simStatus: string | null;
  iccid: string | null;
  phoneNumber: string | null;
  phoneCountryCode: string | null;
  networkStatus: string | null;
  connectedWifiSsid: string | null;
  ipAddress: string | null;
};